/* global DetectUserAgent */
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { isNil } from "ramda";

import { useFacebookActions } from "store/FacebookSlice";
import {
  init,
  login,
  getAllAlbums,
  getImagesOfAlbum,
  checkLoggedin,
  getNextPage,
  validatePermissions,
  customLogin,
} from "utils/facebookUtils";
import { jjLogger } from "utils/logUtils";

import useSettings from "./useSettings";
import useScreen from "./useScreen";

const FACEBOOK_TOKEN_KEY = "fbToken";

const useFacebook = (initialize = false, isOauth = false) => {
  const {
    loadAlbums,
    loadPhotos,
    loadAdditionalPhotos,
    loadAdditionalAlbums,
    changeMode,
    changeModalOpen,
    changeSelectedPhoto,
    changeInitialized,
    changeStatus,
    clearPhotos,
    clearSelectedPhoto,
    resetMode,
    clearAlbums,
    clearStatus,
    changeLoading,
    changeNoticeModal,
  } = useFacebookActions();
  const [nextPage, setNextPage] = useState(null);
  const {
    settings: { fbAppId },
  } = useSettings();
  const isFB4Android = DetectUserAgent.isFB4Android();
  const { isDesktop } = useScreen();

  const { albums, mode, photos, modalOpen, selectedPhoto, initialized, status, showNoticeModal } = useSelector(
    (state) => state.FacebookSlice,
  );
  const loading = useSelector((state) => (isOauth ? true : state.FacebookSlice.loading));

  useEffect(() => {
    if (initialize && !initialized) {
      init(fbAppId, changeInitialized).catch(() => {
        changeLoading(false);
        jjLogger.logDebug("facebookUtils - init() - error initializing facebook sdk");
      });
    }
  }, []);

  const storeFacebookToken = (token) => {
    sessionStorage.setItem(FACEBOOK_TOKEN_KEY, token);
  };

  const getFacebookToken = () => sessionStorage.getItem(FACEBOOK_TOKEN_KEY);

  const changeModalOpenStorage = (value) => {
    const isFbInAppBrowser = DetectUserAgent.isFBInAppBrowser();
    if (isFbInAppBrowser) {
      sessionStorage.setItem("facebookModal", value);
    }
    changeModalOpen(value);
  };

  const availableAlbums = albums?.filter((album) => !isNil(album?.cover_photo?.images));

  const hasSelectedPhoto = !isNil(selectedPhoto) && !isNil(selectedPhoto?.source);

  const checkLoggedinStatus = () => {
    return checkLoggedin()
      .then((currentStatus) => validatePermissions(currentStatus, getFacebookToken()))
      .then((res) => {
        changeStatus(res);
        changeLoading(false);
      })
      .catch(() => {
        changeLoading(false);
        jjLogger.logDebug("useFacebook - checkLoggedinStatus() - error getting login status to user");
      });
  };

  const changeNoticeModalOpen = (value) => {
    if (isDesktop) {
      changeNoticeModal(value);
    }
  };

  const loginFacebook = () => {
    changeLoading(true);
    if (isFB4Android) {
      return customLogin(fbAppId);
    }
    changeNoticeModalOpen(true);
    return login(fbAppId)
      .then(changeStatus)
      .then(() => changeNoticeModalOpen(false))
      .catch(() => {
        changeNoticeModalOpen(false);
        changeLoading(false);
        jjLogger.logDebug("useFacebook - loginFacebook() - error when logging in with facebook");
      });
  };

  const getAlbums = () => {
    return getAllAlbums(setNextPage, getFacebookToken())
      .then(loadAlbums)
      .catch(() => {
        jjLogger.logDebug("useFacebook - getAlbums() : error getting user albums");
      });
  };

  const getImages = (albumId) => {
    return getImagesOfAlbum(albumId, setNextPage, getFacebookToken())
      .then(loadPhotos)
      .catch(() => {
        jjLogger.logDebug("useFacebook - getImages() : error getting photos of album");
      });
  };

  const loadNextPage = (onLoaded) => {
    if (!isNil(nextPage)) {
      return getNextPage(nextPage, setNextPage)
        .then(onLoaded)
        .catch(() => {
          jjLogger.logDebug("useFacebook - loadNextPage() : error getting next page of albums or image");
        });
    }
    return null;
  };

  return {
    albums,
    mode,
    photos,
    initialized,
    changeMode,
    changeModalOpenStorage,
    modalOpen,
    availableAlbums,
    selectedPhoto,
    loading,
    changeSelectedPhoto,
    hasSelectedPhoto,
    status,
    loginFacebook,
    loadNextPage,
    loadAdditionalPhotos,
    loadAdditionalAlbums,
    getAlbums,
    getImages,
    checkLoggedinStatus,
    resetMode,
    clearPhotos,
    clearSelectedPhoto,
    clearAlbums,
    clearStatus,
    storeFacebookToken,
    changeNoticeModalOpen,
    showNoticeModal,
  };
};

export default useFacebook;
